import { useEffect, useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { errMsg } from "@/lib/api";
import { useEditor } from "@/components/editor/useEditorState";
import { usd } from "./useAiEdits";
import EstimateNotes from "./EstimateNotes";

// Shows the AI cost estimate (server-side) and runs the operation only with it: the click is the explicit confirmation.
export default function CostButton({ path, body, label, onRun, disabled, busy, testid, hint }) {
  const { api, id } = useEditor();
  const [est, setEst] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);
  const key = JSON.stringify(body || null);
  useEffect(() => {
    setOk(false);
    if (disabled || !body) { setEst(null); setErr(""); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.post(`/projects/${id}${path}`, body); setEst(data.estimate); setErr(""); } catch (e) { setEst(null); setErr(errMsg(e)); }
      setLoading(false);
    }, 450);
    return () => clearTimeout(t);
  }, [key, disabled, path]); // eslint-disable-line react-hooks/exhaustive-deps
  const big = est && est.usd >= 0.5;
  const can = est && !est.blocked && (!big || ok) && !busy && !disabled && !loading;
  return (
    <div className="space-y-2" data-testid={`${testid}-box`}>
      <div className="flex items-center justify-between gap-2 text-[11px] text-slate-600" data-testid={`${testid}-estimate`}>
        {loading ? <span className="inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />Calcolo del costo…</span>
          : est ? <span title={est.note}>Costo stimato <b className="text-slate-900">{usd(est.usd)}</b> · max {usd(est.max_usd)}{est.repair_included ? " (con 1 correzione automatica)" : ""}</span>
            : <span className="text-slate-400">{err || hint || "Completa i campi per vedere il costo stimato"}</span>}
      </div>
      {est && <p className="text-[10.5px] leading-snug text-slate-600" data-testid={`${testid}-note`}>Stima in USD sul listino del provider: i crediti Universal Key effettivamente scalati possono essere diversi.</p>}
      <EstimateNotes est={est} testid={testid} />
      {big && !est.blocked && (
        <label className="flex items-start gap-2 text-[11px] text-slate-700">
          <Checkbox checked={ok} onCheckedChange={(v) => setOk(!!v)} className="mt-0.5" data-testid={`${testid}-confirm-checkbox`} />Confermo il costo stimato di {usd(est.usd)} (fino a {usd(est.max_usd)})
        </label>
      )}
      <Button size="sm" className="w-full h-9 bg-sky-600 hover:bg-sky-700" disabled={!can} onClick={() => onRun(est)} data-testid={testid}>
        {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Wand2 size={14} className="mr-1.5" />}{label}{est ? ` · ${usd(est.usd)}` : ""}
      </Button>
    </div>
  );
}
